import { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';
import {
  addDays,
  addMonths,
  formatDayLabel,
  monthStartOf,
  weekDaysFrom,
  weekStartOf,
} from '../../utils/leave-calendar-week';

type CalendarView = 'week' | 'month';

interface LeaveCalendarPeriodProps {
  view: CalendarView;
  /** First day of the period shown ("YYYY-MM-DD"). */
  firstDay: string;
  /** The period's own label ("26 Oct - 1 Nov 2026", "October 2026"). */
  label: string;
  /** Whether the period shown holds today: "This week" / "This month" shows only when it does not. */
  isCurrentPeriod: boolean;
  today: string;
  locale: string;
  onStep: (direction: 1 | -1) => void;
  /** A picked day: the week's Monday, or the first of a month. */
  onPick: (day: string) => void;
  onToday: () => void;
}

/** The week popover (prototype `wkHtml`): a month of week rows; a whole row is picked at once. */
const WeekPopover = ({
  weekStart,
  today,
  locale,
  onPick,
}: {
  weekStart: string;
  today: string;
  locale: string;
  onPick: (weekStart: string) => void;
}) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(monthStartOf(addDays(weekStart, 3)));
  const start = weekStartOf(month);
  const rows: string[] = [];
  for (let week = 0; week < 6; week += 1) {
    const rowStart = addDays(start, week * 7);
    if (week > 3 && rowStart.slice(0, 7) !== month.slice(0, 7)) break;
    rows.push(rowStart);
  }

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('LEAVE_CALENDAR_PREVIOUS_MONTH')}
          onClick={() => setMonth(addMonths(month, -1))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <b className="text-sm font-medium" aria-live="polite">
          {formatDayLabel(month, locale, { month: 'long', year: 'numeric' })}
        </b>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('LEAVE_CALENDAR_NEXT_MONTH')}
          onClick={() => setMonth(addMonths(month, 1))}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
      <div className="grid gap-0.5">
        <div className="grid grid-cols-[repeat(7,34px)]" aria-hidden>
          {weekDaysFrom(start).map((day) => (
            <span key={day} className="grid h-7 place-items-center text-xs text-muted-foreground">
              {formatDayLabel(day, locale, { weekday: 'short' }).slice(0, 2)}
            </span>
          ))}
        </div>
        {rows.map((rowStart) => (
          <button
            key={rowStart}
            type="button"
            aria-pressed={rowStart === weekStart}
            aria-label={`${formatDayLabel(rowStart, locale, { day: 'numeric', month: 'short' })} - ${formatDayLabel(addDays(rowStart, 6), locale, { day: 'numeric', month: 'short', year: 'numeric' })}`}
            onClick={() => onPick(rowStart)}
            className="group grid h-8 grid-cols-[repeat(7,34px)] items-center rounded-md text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 aria-pressed:bg-primary aria-pressed:text-primary-foreground"
          >
            {weekDaysFrom(rowStart).map((day) => (
              <span
                key={day}
                className={cn(
                  'grid h-8 place-items-center tabular-nums',
                  day.slice(0, 7) !== month.slice(0, 7) &&
                    'text-muted-foreground group-aria-pressed:text-primary-foreground',
                  day === today && 'font-semibold underline decoration-2 underline-offset-4'
                )}
              >
                {Number(day.slice(8))}
              </span>
            ))}
          </button>
        ))}
      </div>
    </div>
  );
};

/** The month popover (prototype `calMonHtml`): a year with its twelve months. */
const MonthPopover = ({
  month,
  today,
  locale,
  onPick,
}: {
  month: string;
  today: string;
  locale: string;
  onPick: (monthStart: string) => void;
}) => {
  const { t } = useTranslation();
  const [year, setYear] = useState(Number(month.slice(0, 4)));
  const months = Array.from(
    { length: 12 },
    (_, index) => `${year}-${String(index + 1).padStart(2, '0')}-01`
  );

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('LEAVE_CALENDAR_PREVIOUS_YEAR')}
          onClick={() => setYear(year - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <b className="text-sm font-semibold tabular-nums" aria-live="polite">
          {year}
        </b>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('LEAVE_CALENDAR_NEXT_YEAR')}
          onClick={() => setYear(year + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {months.map((monthStart) => (
          <button
            key={monthStart}
            type="button"
            aria-pressed={monthStart === month}
            onClick={() => onPick(monthStart)}
            className={cn(
              'rounded-md border bg-card px-2.5 py-2 text-left text-sm font-medium outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              monthStart === monthStartOf(today) && 'border-primary',
              'aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground'
            )}
          >
            {formatDayLabel(monthStart, locale, { month: 'short' })}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * The period controls of the calendar toolbar (prototype `.wkp`): previous, a trigger with the period
 * that opens a picker, next, and "This week" / "This month" while the period shown is not the current one.
 */
export const LeaveCalendarPeriod = ({
  view,
  firstDay,
  label,
  isCurrentPeriod,
  today,
  locale,
  onStep,
  onPick,
  onToday,
}: LeaveCalendarPeriodProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isWeek = view === 'week';
  const pick = (day: string) => {
    setOpen(false);
    onPick(day);
  };

  return (
    <div
      className="inline-flex flex-wrap items-center gap-2"
      role="group"
      aria-label={t(isWeek ? 'LEAVE_CALENDAR_WEEK' : 'LEAVE_CALENDAR_MONTH')}
    >
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t(isWeek ? 'LEAVE_CALENDAR_PREVIOUS_WEEK' : 'LEAVE_CALENDAR_PREVIOUS_MONTH')}
        onClick={() => onStep(-1)}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn('justify-start', isWeek ? 'min-w-[220px]' : 'min-w-[180px]')}
            aria-label={`${label}. ${t(isWeek ? 'LEAVE_CALENDAR_CHOOSE_WEEK' : 'LEAVE_CALENDAR_CHOOSE_MONTH')}`}
          >
            <Calendar className="size-4 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          {isWeek ? (
            <WeekPopover weekStart={firstDay} today={today} locale={locale} onPick={pick} />
          ) : (
            <MonthPopover month={firstDay} today={today} locale={locale} onPick={pick} />
          )}
        </PopoverContent>
      </Popover>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t(isWeek ? 'LEAVE_CALENDAR_NEXT_WEEK' : 'LEAVE_CALENDAR_NEXT_MONTH')}
        onClick={() => onStep(1)}
      >
        <ChevronRight className="size-4" />
      </Button>
      {!isCurrentPeriod && (
        <Button type="button" variant="ghost" onClick={onToday}>
          {t(isWeek ? 'LEAVE_CALENDAR_THIS_WEEK' : 'LEAVE_CALENDAR_THIS_MONTH')}
        </Button>
      )}
    </div>
  );
};
